import { keepPreviousData } from '@tanstack/react-query';
/**
 * Clinic portal massager hooks.
 * All API calls go through @/services/main/massagers. Query error surfaced via toast.
 */

import { useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/shared/providers/AuthContext';
import {
  getMassagerInit,
  searchMassagers,
  getMassagerDetail,
  getAllMassagers,
  exportMassagersCsv,
  updateMassagerDetail,
  bulkUpdateMassagerAlert,
} from '@/services/main/massagers';
import type {
  MassagerSearchFilters,
  BulkUpdateAlertRequest,
  MassagerUpdateRequest,
} from '@/services/main/massagers';
import type {
  MassagerInitResponse,
  MassagerSearchResponse,
  MassagerDetailResponse,
  BulkUpdateResponse,
} from '@/pages/main/massager/types';
import { getApiErrorMessage, DEFAULT_FETCH_ERROR_MESSAGE, isNotFoundError } from '@/utils/apiError';
import { SERVICE_TYPE_A2, SERVICE_TYPE_R2 } from '@/constants/serviceType';
import { useClinicContext } from './useClinicContext';
import useAppToast from '@/hooks/useAppToast';

/** Current X-Service-Type from localStorage (A2/R2). */
function getServiceTypeHeader(): string {
  return localStorage.getItem('service_type') || SERVICE_TYPE_R2;
}

// ==================== Query Hooks ====================

/**
 * Fetches massager init data (checkin_enabled, prefectures, health_center_regist_types).
 * Returns list data and loading state; fetch error is shown via toast inside the hook.
 */
export function useMainMassagerInit() {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = getServiceTypeHeader();
  const toast = useAppToast();
  const { data, isLoading, isError, isPending, refetch, error } = useQuery<
    MassagerInitResponse,
    Error
  >({
    queryKey: ['massagers', 'init', tenant, clinicNumber, serviceType],
    queryFn: () => getMassagerInit(tenant, clinicNumber, serviceType),
    enabled: !!tenant && !!clinicNumber,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isError, isPending, refetch, error };
}

/**
 * Searches massagers with filters. Returns list data and loading state; fetch error is shown via toast.
 */
export function useMainMassagerSearch(filters: MassagerSearchFilters) {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = getServiceTypeHeader();
  const toast = useAppToast();
  const { data, isLoading, isError, isPending, isFetching, refetch, error } = useQuery<
    MassagerSearchResponse,
    Error
  >({
    queryKey: ['massagers', 'search', tenant, clinicNumber, serviceType, filters],
    queryFn: () => searchMassagers(tenant, clinicNumber, filters, serviceType),
    enabled: !!tenant && !!clinicNumber,
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isError, isPending, isFetching, refetch, error };
}

/**
 * Fetches a single massager detail by ID. Returns data and loading state; fetch error is shown via toast.
 */
export function useMainMassagerDetail(id: number) {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = getServiceTypeHeader();
  const toast = useAppToast();
  const { data, isLoading, isError, isPending, isFetching, refetch, error } = useQuery<
    MassagerDetailResponse,
    Error
  >({
    queryKey: ['massagers', 'detail', tenant, clinicNumber, serviceType, id],
    queryFn: () => getMassagerDetail(tenant, clinicNumber, id, serviceType),
    enabled: !!tenant && !!clinicNumber && !!id,
    meta: { showNotFoundToast: true },
  });

  useEffect(() => {
    if (isError && error && !isNotFoundError(error)) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isError, isPending, isFetching, refetch, error };
}

/**
 * Fetches all massagers (paginated list).
 * Returns data and loading state; fetch error is shown via toast inside the hook.
 */
export function useMainMassagers(perPage: number = 20, page: number = 1) {
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = getServiceTypeHeader();
  const toast = useAppToast();
  const { data, isLoading, isError, isPending, refetch, error } = useQuery<
    MassagerSearchResponse,
    Error
  >({
    queryKey: ['massagers', 'list', tenant, clinicNumber, serviceType, perPage, page],
    queryFn: () => getAllMassagers(tenant, clinicNumber, perPage, page, serviceType),
    enabled: !!tenant && !!clinicNumber,
  });

  useEffect(() => {
    if (isError && error) {
      toast.error(getApiErrorMessage(error, DEFAULT_FETCH_ERROR_MESSAGE));
    }
  }, [isError, error, toast]);

  return { data, isLoading, isError, isPending, refetch, error };
}

// ==================== Mutation Hooks ====================

/**
 * Exports massagers to CSV and triggers download. Returns mutateAsync and loading state.
 */
export function useMainMassagerExportCsv() {
  const { clinic } = useAuth();
  const { tenant, clinicNumber } = useClinicContext();
  const raw = clinic?.service_type?.toString().toUpperCase();
  const effectiveServiceType: 'A2' | 'R2' =
    raw === SERVICE_TYPE_A2 || raw === SERVICE_TYPE_R2 ? raw : (getServiceTypeHeader() as 'A2' | 'R2');

  const { mutateAsync: exportCsvMutation, isPending: isLoading } = useMutation({
    mutationFn: (filters?: MassagerSearchFilters) =>
      exportMassagersCsv(tenant, clinicNumber, filters, effectiveServiceType),
    meta: {
      errorMessage: 'CSV出力に失敗しました。',
    },
  });

  return { exportCsvMutation, isLoading };
}

/**
 * Bulk update alert settings for selected massagers (一括設定). Returns mutateAsync and loading state.
 */
export function useMainMassagerBulkUpdateAlert() {
  const toast = useAppToast();
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = getServiceTypeHeader();

  const { mutateAsync: bulkUpdateMutation, isPending: isLoading } = useMutation({
    mutationFn: (data: BulkUpdateAlertRequest) =>
      bulkUpdateMassagerAlert(tenant, clinicNumber, data, serviceType),
    onSuccess: (data: BulkUpdateResponse) => {
      toast.success(data?.data?.message ?? '一括設定を更新しました。');
    },
    onError: error => {
      const msg = getApiErrorMessage(error, '更新に失敗しました。');
      toast.error(msg);
    },
  });

  return { bulkUpdateMutation, isLoading };
}

/**
 * Updates single massager detail (email, check-in settings). Returns mutateAsync and loading state.
 */
export function useMainMassagerUpdate() {
  const queryClient = useQueryClient();
  const toast = useAppToast();
  const { tenant, clinicNumber } = useClinicContext();
  const serviceType = getServiceTypeHeader();

  const { mutateAsync: updateMutation, isPending: isLoading } = useMutation({
    mutationFn: ({ id, data }: { id: number; data: MassagerUpdateRequest }) =>
      updateMassagerDetail(tenant, clinicNumber, id, data, serviceType),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ['massagers', 'detail', tenant, clinicNumber, serviceType, variables.id],
      });
      toast.success('更新が正常に終了しました。');
    },
    onError: error => {
      const msg = getApiErrorMessage(error, 'エラーが発生しました。');
      toast.error(msg);
    },
  });

  return { updateMutation, isLoading };
}
